import {
  useCreateMultipleprintHistory,
  useDeleteMultipleprintHistory,
  useEditMultipleprintHistory,
  useGetHistoryOfMultipleprint,
  useGetHistoryOfMultipleprintMerged,
  useReorderMultipleprintProvenance,
  useUpdateMultipleprintHistorySequence,
  type MultipleprintHistory,
  type MultipleprintHistoryMergedAuction,
} from "@/api/multipleprint/multipleprint-history";
import * as AF from "@/components/form-items/AbstractForm";
import {
  MaskableTableSection,
  MaskableTableSectionBody,
  MaskableTableSectionHeader,
} from "@/components/form-items/MaskableTableSection";
import { BooleanBadge } from "@/components/ui/boolean-badge";
import {
  AddButton,
  CancelButton,
  DeleteButton,
  SaveButton,
  UpdateButton,
} from "@/components/ui/custom-buttons";
import {
  CustomDialogContent,
  CustomDialogFooter,
  CustomDialogTitle,
} from "@/components/ui/custom-dialog";
import { DataTable } from "@/components/ui/datatable";
import { Dialog, DialogClose, DialogHeader } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { RegisterVisibilityField } from "@/context/FormFieldVisibilityContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = z.object({
  estampesMultiplesHistoriqueDescriptionNom: z.string(),
  estampesMultiplesHistoriqueDescriptionVille: z.string(),
  estampesMultiplesHistoriqueDescriptionDates: z.string(),
  estampesMultiplesHistoriqueDescriptionCollectionPart: z.boolean(),
});

type MultipleprintHistoryForm = z.infer<typeof formSchema>;

const HISTORY_TABLE_MASK_GROUP = "estampesMultiplesHistoriqueTableGroup";

/** Dialog inputs unmount when closed — keep them in the Champs drawer. */
const HISTORY_DIALOG_FIELDS = [
  {
    name: "estampesMultiplesHistoriqueDescriptionNom",
    labelKey: "oeuvresLocalisationHistoriqueHistoriqueNom",
  },
  {
    name: "estampesMultiplesHistoriqueDescriptionVille",
    labelKey: "oeuvresLocalisationHistoriqueHistoriqueVille",
  },
  {
    name: "estampesMultiplesHistoriqueDescriptionDates",
    labelKey: "oeuvresLocalisationHistoriqueHistoriqueDates",
  },
] as const;

type CreateOrEditMultipleprintHistoryProps = {
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  open: boolean;
  multipleprintHistory?: MultipleprintHistory;
  multipleprintId: string;
};

export const CreateOrEditMultipleprintHistory = ({
  disabled = false,
  onOpenChange,
  open,
  multipleprintHistory,
  multipleprintId,
}: CreateOrEditMultipleprintHistoryProps) => {
  const isEdit = multipleprintHistory !== undefined;

  const updater = useEditMultipleprintHistory();
  const creator = useCreateMultipleprintHistory();
  const deleter = useDeleteMultipleprintHistory();

  const { t } = useTranslation();

  const form = useForm<MultipleprintHistoryForm>({
    defaultValues: isEdit
      ? {
          estampesMultiplesHistoriqueDescriptionCollectionPart:
            multipleprintHistory.estampesMultiplesHistoriqueDescriptionCollectionPart,
          estampesMultiplesHistoriqueDescriptionDates:
            multipleprintHistory.estampesMultiplesHistoriqueDescriptionDates,
          estampesMultiplesHistoriqueDescriptionNom:
            multipleprintHistory.estampesMultiplesHistoriqueDescriptionNom,
          estampesMultiplesHistoriqueDescriptionVille:
            multipleprintHistory.estampesMultiplesHistoriqueDescriptionVille,
        }
      : {
          estampesMultiplesHistoriqueDescriptionCollectionPart: false,
          estampesMultiplesHistoriqueDescriptionDates: "",
          estampesMultiplesHistoriqueDescriptionNom: "",
          estampesMultiplesHistoriqueDescriptionVille: "",
        },
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (multipleprintHistory) {
      form.reset({
        estampesMultiplesHistoriqueDescriptionCollectionPart:
          multipleprintHistory.estampesMultiplesHistoriqueDescriptionCollectionPart,
        estampesMultiplesHistoriqueDescriptionDates:
          multipleprintHistory.estampesMultiplesHistoriqueDescriptionDates,
        estampesMultiplesHistoriqueDescriptionNom:
          multipleprintHistory.estampesMultiplesHistoriqueDescriptionNom,
        estampesMultiplesHistoriqueDescriptionVille:
          multipleprintHistory.estampesMultiplesHistoriqueDescriptionVille,
      });
    }
  }, [multipleprintHistory, form]);

  function onSubmit(values: MultipleprintHistoryForm) {
    if (isEdit && multipleprintHistory) {
      updater.mutate(
        {
          historyId: multipleprintHistory.id,
          multipleprintId:
            multipleprintHistory.multipleprintId ?? multipleprintId,
          history: values,
        },
        { onSuccess: () => onOpenChange?.(false) },
      );
      return;
    }

    creator.mutate(
      { history: values, multipleprintId },
      {
        onSuccess: () => {
          form.reset();
          onOpenChange?.(false);
        },
      },
    );
  }

  function onDelete() {
    if (!isEdit || !multipleprintHistory) return;
    deleter.mutate(
      {
        multipleprintId:
          multipleprintHistory.multipleprintId ?? multipleprintId,
        historyId: multipleprintHistory.id,
      },
      {
        onSuccess: () => onOpenChange?.(false),
      },
    );
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <CustomDialogContent>
        <Form {...form}>
          <form
            className="flex flex-col"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <DialogHeader>
              <CustomDialogTitle>{t("addArtworkHistory")}</CustomDialogTitle>
            </DialogHeader>
            <AF.Col className="p-6">
              <AF.Text
                name="estampesMultiplesHistoriqueDescriptionNom"
                label={t("oeuvresLocalisationHistoriqueHistoriqueNom")}
              />
              <AF.Text
                name="estampesMultiplesHistoriqueDescriptionVille"
                label={t("oeuvresLocalisationHistoriqueHistoriqueVille")}
              />
              <AF.Text
                name="estampesMultiplesHistoriqueDescriptionDates"
                label={t("oeuvresLocalisationHistoriqueHistoriqueDates")}
              />
              <AF.Checkbox
                name="estampesMultiplesHistoriqueDescriptionCollectionPart"
                label={t(
                  "oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere",
                )}
              />
              {isEdit && (
                <DeleteButton
                  variant={"link"}
                  className="text-red-600 w-fit m-auto"
                  iconProps={{ className: "stroke-red-600" }}
                  onClick={onDelete}
                  disabled={disabled}
                >
                  {t("supprimerOeuvresLocalisation")}
                </DeleteButton>
              )}
            </AF.Col>
            <CustomDialogFooter>
              <DialogClose asChild>
                <CancelButton className="flex-1" />
              </DialogClose>
              {isEdit ? (
                <UpdateButton
                  type="submit"
                  className="flex-1"
                  disabled={disabled}
                />
              ) : (
                <SaveButton
                  type="submit"
                  className="flex-1"
                  disabled={disabled}
                />
              )}
            </CustomDialogFooter>
          </form>
        </Form>
      </CustomDialogContent>
    </Dialog>
  );
};

export const MultipleprintHistoryTable = ({
  multipleprintId,
  disabled = false,
  provenanceMode = false,
}: {
  multipleprintId?: string;
  disabled?: boolean;
  provenanceMode?: boolean;
}) => {
  const { t } = useTranslation();

  type HistoryRow =
    | (MultipleprintHistory & { kind: "history" })
    | (MultipleprintHistoryMergedAuction & { kind: "auction" });

  const isHistoryRow = (
    row: HistoryRow,
  ): row is MultipleprintHistory & { kind: "history" } =>
    row.kind === "history";

  const { data: historyOnly = [] } = useGetHistoryOfMultipleprint(
    provenanceMode ? undefined : multipleprintId,
  );
  const { data: merged } = useGetHistoryOfMultipleprintMerged(
    multipleprintId,
    provenanceMode,
  );

  const [rows, setRows] = useState<HistoryRow[]>([]);

  const [openEditForm, setOpenEditForm] = useState(false);
  const [openCreateForm, setOpenCreateForm] = useState(false);

  const historyToUpdate = useRef<MultipleprintHistory>();

  const sequenceMutator = useUpdateMultipleprintHistorySequence();
  const provenanceReorderMutator = useReorderMultipleprintProvenance();

  function handleUpdate(multipleprintHistory: MultipleprintHistory) {
    historyToUpdate.current = multipleprintHistory;
    setOpenEditForm(true);
  }

  const datatableColumns = useMemo(
    (): ColumnDef<HistoryRow>[] => [
      {
        size: 1000,
        header: t("oeuvresLocalisationHistoriqueHistoriqueNom"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r)) {
            return r.estampesMultiplesHistoriqueDescriptionNom;
          }
          const parts = [
            r.auction?.ventesDescriptionTitre,
            r.ventesDansEstampesMultiplesDescriptionNumeroLot
              ? `lot n° ${r.ventesDansEstampesMultiplesDescriptionNumeroLot}`
              : null,
          ].filter(Boolean);
          return parts.length ? parts.join(" - ") : t("entryauctions");
        },
      },
      {
        header: t("oeuvresLocalisationHistoriqueHistoriqueVille"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r)) {
            return r.estampesMultiplesHistoriqueDescriptionVille;
          }
          const parts = [
            r.auction?.ventesDescriptionLieu,
            r.auction?.ventesDescriptionVille,
            r.auction?.ventesDescriptionPays,
          ].filter(Boolean);
          return parts.join(", ");
        },
      },
      {
        header: t("oeuvresLocalisationHistoriqueHistoriqueDates"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r)) {
            return r.estampesMultiplesHistoriqueDescriptionDates;
          }
          return r.auction?.ventesDescriptionDate ?? "";
        },
      },
      {
        header: t(
          "oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere",
        ),
        cell: (v) => (
          <BooleanBadge
            value={
              isHistoryRow(v.row.original)
                ? v.row.original
                    .estampesMultiplesHistoriqueDescriptionCollectionPart
                : false
            }
          />
        ),
      },
    ],
    [t],
  );

  useEffect(() => {
    if (provenanceMode && merged) {
      setRows([...merged.timeline]);
      return;
    }
    setRows(
      historyOnly.map<HistoryRow>((h) => ({ ...h, kind: "history" as const })),
    );
  }, [historyOnly, merged, provenanceMode]);

  const handleRowDragged = (newData: HistoryRow[]) => {
    if (!multipleprintId) return;
    setRows(newData);
    if (provenanceMode) {
      provenanceReorderMutator.mutate({
        multipleprintId,
        timeline: newData.map((d) => ({ kind: d.kind, id: d.id })),
      });
      return;
    }
    sequenceMutator.mutate({
      newSequence: newData.filter(isHistoryRow).map((d) => d.id),
      multipleprintId,
    });
  };

  const titleLabel = t(
    provenanceMode
      ? "multipleprint.table.provenance.title"
      : "multipleprint.table.history.title",
  );

  const addButton = (
    <AddButton
      type="button"
      onClick={() => setOpenCreateForm(true)}
      disabled={disabled}
    />
  );

  if (!multipleprintId) {
    return null;
  }

  return (
    <>
      {HISTORY_DIALOG_FIELDS.map(({ name, labelKey }) => (
        <RegisterVisibilityField
          key={name}
          name={name}
          label={t(labelKey)}
        />
      ))}
      <MaskableTableSection>
        <CreateOrEditMultipleprintHistory
          multipleprintId={multipleprintId}
          multipleprintHistory={historyToUpdate.current}
          open={openEditForm}
          onOpenChange={setOpenEditForm}
          disabled={disabled}
        />
        <CreateOrEditMultipleprintHistory
          open={openCreateForm}
          onOpenChange={setOpenCreateForm}
          multipleprintId={multipleprintId}
          disabled={disabled}
        />
        <MaskableTableSectionHeader
          groupName={HISTORY_TABLE_MASK_GROUP}
          label={titleLabel}
          trailing={addButton}
        />
        <MaskableTableSectionBody groupName={HISTORY_TABLE_MASK_GROUP}>
          <DataTable
            enableDragRows
            onRowDragged={(d) => handleRowDragged(d.newData)}
            columns={datatableColumns}
            data={rows}
            onRowClick={(e) => {
              if (isHistoryRow(e.original)) handleUpdate(e.original);
            }}
          />
        </MaskableTableSectionBody>
      </MaskableTableSection>
    </>
  );
};
